import type { PlayerId } from '@/components'
import { defineEvent } from '@/core/EventBus'
import type { Entity } from '@/core/ecs/Entity'

export type HitKind = 'push' | 'dash' | 'wave' | 'vortex' | 'explosion' | 'hazard' | 'bump'

export interface HitEvent {
  source: Entity
  target: Entity
  /** World position of the impact, for VFX and stereo panning. */
  x: number
  z: number
  /** Impulse direction (normalised) and magnitude in m/s. */
  dirX: number
  dirZ: number
  power: number
  instability: number
  kind: HitKind
  /** True when the target was bracing and soaked most of it. */
  braced: boolean
  /**
   * True when the impulse was strong enough to put the target into the authored
   * launch state, as opposed to nudging them. Presentation splits on this: a
   * launch is the game's headline event and a nudge must never look like one.
   */
  launched: boolean
  /**
   * True when the launch, left alone, ends outside the platform. It is a
   * prediction, not a verdict — the victim can still drift or bounce back —
   * but it is what lets the moment be announced while it is still happening,
   * the way Smash announces a `tumble`.
   */
  lethal: boolean
}

export interface AbilityEvent {
  entity: Entity
  player: PlayerId
  ability: number
  phase: 'cast' | 'fire' | 'end' | 'fail'
  x: number
  z: number
  angle: number
}

export interface ExplosionEvent {
  x: number
  z: number
  radius: number
  power: number
  kind: 'barrel' | 'vortex'
}

export interface FallEvent {
  entity: Entity
  player: PlayerId | null
  x: number
  z: number
}

export interface RoundEvent {
  phase: 'countdown' | 'fight' | 'over' | 'matchOver'
  winner: PlayerId | null
}

export interface ShakeEvent {
  strength: number
  x: number
  z: number
}

export interface HazardEvent {
  entity: Entity
  kind: 'burnerCharge' | 'burnerFire'
  x: number
  z: number
  radius: number
}

export interface PickupEvent {
  entity: Entity
  player: PlayerId
  kind: 'shield' | 'haste'
  x: number
  z: number
}

/** A launched fighter touching the slab — one skip of the stone. */
export interface BounceEvent {
  entity: Entity
  x: number
  z: number
  speed: number
  /** 1 for the first contact, 2 for the second, and so on. */
  index: number
}

/** The plasma bolt skipping off the floor or ricocheting off a prop. */
export interface BoltBounceEvent {
  entity: Entity
  player: PlayerId
  x: number
  z: number
  kind: 'floor' | 'prop' | 'expire'
}

export interface ArenaCollapseEvent {
  /** Effective radius after the collapse tick, for VFX and audio cues. */
  scale: number
  starting: boolean
}

/** Which personal move fired. One per entry of the `SIGNATURES` table. */
export type SignatureMove =
  | 'barrier'
  | 'charge'
  | 'toss'
  | 'lunge'
  | 'gust'
  | 'retreat'
  | 'crate'
  | 'slam'
  | 'swap'
  | 'roll'
  | 'wave'
  | 'anomaly'

/**
 * A personal move firing, with the shape of what it actually did.
 *
 * The generic `Ability` event carries only the *slot*, and presentation keyed on
 * the slot draws the same eight-metre shockwave for a two-metre scoop, a thrown
 * crate and a gust — the effect stops describing the move and starts lying about
 * it. Radius and endpoints are reported by the move itself, so the effect can
 * never claim more reach than the move has.
 */
export interface SignatureEvent {
  entity: Entity
  player: PlayerId
  move: SignatureMove
  x: number
  z: number
  angle: number
  /** Reach of what happened, in metres. */
  radius: number
  /** Second endpoint, for moves that act somewhere other than the caster. */
  toX: number
  toZ: number
  /** Whether it connected. A whiff must not look like a hit. */
  hit: boolean
}

export const Events = {
  Hit: defineEvent<HitEvent>('Hit'),
  Ability: defineEvent<AbilityEvent>('Ability'),
  Explosion: defineEvent<ExplosionEvent>('Explosion'),
  Fall: defineEvent<FallEvent>('Fall'),
  Round: defineEvent<RoundEvent>('Round'),
  Shake: defineEvent<ShakeEvent>('Shake'),
  Hazard: defineEvent<HazardEvent>('Hazard'),
  Pickup: defineEvent<PickupEvent>('Pickup'),
  Bounce: defineEvent<BounceEvent>('Bounce'),
  BoltBounce: defineEvent<BoltBounceEvent>('BoltBounce'),
  ArenaCollapse: defineEvent<ArenaCollapseEvent>('ArenaCollapse'),
  Signature: defineEvent<SignatureEvent>('Signature'),
} as const
